import { useEffect, useMemo, useState } from 'react';
import { Bell, ChevronLeft, ChevronRight, MoreHorizontal, PackageCheck, Search, ShoppingBag, StickyNote } from 'lucide-react';

import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { SoftCard } from '@/components/ui/soft-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { OrderConfirmModal } from '@/components/orders/OrderConfirmModal';
import { OrderDetailModal } from '@/components/orders/OrderDetailModal';
import { OrderUpdateModal } from '@/components/orders/OrderUpdateModal';
import { StatusFilter } from '@/components/orders/StatusFilter';
import { ORDER_DISPLAY_BUCKETS, StatusPill, bucketFor, expandBuckets } from '@/components/orders/StatusPill';
import { isRefundPending, netOrderTotal, orderCurrency } from '@/components/orders/order-totals';
import { useCheckPermission } from '@/hooks/useCheckPermission';
import { useOrderInsights } from '@/hooks/useOrderInsights';
import { useOrders } from '@/hooks/useOrders';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { cn } from '@/lib/utils';
import { PageScore } from '@/components/overview/LocalPotentialHeader';

const PAGE_SIZE = 20;
const ORDER_TYPE_LABEL = {
	reservation: 'Reservation',
	'click-collect': 'Click & Collect'
};

// An order is "active" — i.e. needs the retailer to do something — while it's
// awaiting confirmation or pickup. These are pinned into their own section.
function isActiveStatus(status) {
	const bucket = bucketFor(status);
	return bucket === 'Pending' || bucket === 'Confirmed';
}

function matchesQuery(order, q) {
	if (!q) return true;
	const idMatch = String(order.id || '')
		.toLowerCase()
		.includes(q);
	const refMatch = String(order.pickupReference || '')
		.toLowerCase()
		.includes(q);
	const customerMatch = String(order.customerName || '')
		.toLowerCase()
		.includes(q);
	return idMatch || refMatch || customerMatch;
}

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
	day: 'numeric',
	month: 'short',
	year: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
	hour12: false
});

function formatOrderDate(order) {
	const ts = typeof order.createdAt === 'number' ? order.createdAt : Date.parse(order.createdAt || '');
	return Number.isFinite(ts) ? dateFormatter.format(ts) : '';
}

function formatCurrency(amount, currency = 'GBP') {
	return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
}

function sortByDateDesc(a, b) {
	const tsA = typeof a.createdAt === 'number' ? a.createdAt : Date.parse(a.createdAt || '');
	const tsB = typeof b.createdAt === 'number' ? b.createdAt : Date.parse(b.createdAt || '');
	return (tsB || 0) - (tsA || 0);
}

function buildPageNumbers(currentPage, totalPages) {
	if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
	const pages = [];
	const showLeftEdge = currentPage <= 4;
	const showRightEdge = currentPage >= totalPages - 3;
	if (showLeftEdge) {
		pages.push(1, 2, 3, 4, 5, 'ellipsis', totalPages);
	} else if (showRightEdge) {
		pages.push(1, 'ellipsis', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
	} else {
		pages.push(1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages);
	}
	return pages;
}

function KpiSoftCard({ label, value, sub }) {
	return (
		<SoftCard>
			<div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
			<div className="mt-3 text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">{value}</div>
			{sub ? <div className="mt-2 text-sm text-muted-foreground">{sub}</div> : null}
		</SoftCard>
	);
}

function OrdersSkeleton() {
	return (
		<div className="space-y-8">
			<header>
				<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Orders</h1>
				<p className="mt-2 text-muted-foreground">Manage your customers' orders. New orders will show on the page automatically.</p>
			</header>
			<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
				{[0, 1, 2].map((i) => (
					<SoftCard key={i}>
						<Skeleton className="h-3 w-1/3" />
						<Skeleton className="mt-4 h-8 w-2/3" />
						<Skeleton className="mt-3 h-3 w-1/2" />
					</SoftCard>
				))}
			</div>
			<SoftCard className="!p-0">
				<div className="space-y-2 p-4">
					{[0, 1, 2, 3, 4].map((i) => (
						<Skeleton key={i} className="h-10 w-full" />
					))}
				</div>
			</SoftCard>
		</div>
	);
}

function RowActions({ order, canManage, onStartConfirm, onStartUpdate, onMarkCompleted, onStartRefund }) {
	if (order.status === 'refund_failed') {
		return <span className="text-xs font-semibold text-destructive">Contact Customer Success</span>;
	}
	const isPending = order.status === 'reserved' || order.status === 'paid';
	const isConfirmed = order.status === 'confirmed';
	// A partially refunded order still has a live remainder: the customer
	// collects it (Mark completed), rejects it (Cancel — refunds the remaining
	// balance) or the shopkeeper refunds further lines (Refund items).
	const isPartiallyRefunded = order.status === 'partially_refunded';
	const canRefundItems = order.orderType === 'click-collect' && (isConfirmed || isPartiallyRefunded);
	if (!isPending && !isConfirmed && !isPartiallyRefunded) return null;

	const tooltip = canManage ? undefined : "You don't have permission to manage orders";

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					data-testid={`order-actions-${order.id}`}
					className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					aria-label="Action needed"
					onClick={(e) => e.stopPropagation()}
				>
					<MoreHorizontal className="h-4 w-4" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-44">
				{isPending ? (
					<DropdownMenuItem
						disabled={!canManage}
						onSelect={() => onStartConfirm(order)}
						title={tooltip}
						className="cursor-pointer"
					>
						Confirm
					</DropdownMenuItem>
				) : null}
				{isConfirmed || isPartiallyRefunded ? (
					<DropdownMenuItem
						disabled={!canManage}
						onSelect={() => onMarkCompleted({ ...order, status: 'completed' })}
						title={tooltip}
						className="cursor-pointer"
					>
						Mark completed
					</DropdownMenuItem>
				) : null}
				{canRefundItems ? (
					<DropdownMenuItem
						disabled={!canManage}
						onSelect={() => onStartRefund(order)}
						title={tooltip}
						className="cursor-pointer"
					>
						Refund items
					</DropdownMenuItem>
				) : null}
				<DropdownMenuItem
					disabled={!canManage}
					onSelect={() => onStartUpdate({ ...order, status: 'cancelled' })}
					title={tooltip}
					className="cursor-pointer text-destructive focus:text-destructive"
				>
					Cancel
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

export function Orders() {
	const { shop, isLoading: shopLoading } = useShop();
	const { shopPath } = useShopPath();
	const { orders, isLoading: ordersLoading, updateOrder, refundOrder } = useOrders(shop?.id);
	// The KPI cards are labelled "Past 12 months", so window the insights query
	// to the last 12 months rather than letting the Lambda aggregate all-time.
	// Memoised with empty deps so the SWR key (`…, from, to, …`) stays stable
	// for the session and doesn't trigger a refetch loop on every render.
	const insightsWindow = useMemo(() => {
		const to = new Date();
		const from = new Date(to);
		from.setFullYear(from.getFullYear() - 1);
		return { from: from.toISOString(), to: to.toISOString() };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const { insights } = useOrderInsights(shop?.id, insightsWindow);
	const canManage = useCheckPermission('org:orders:manage');
	const { toast } = useToast();

	const [query, setQuery] = useState('');
	const [statusBuckets, setStatusBuckets] = useState([]);
	const [page, setPage] = useState(1);
	const [activeOrderId, setActiveOrderId] = useState(null);
	const [pendingUpdate, setPendingUpdate] = useState(null);
	const [confirmTarget, setConfirmTarget] = useState(null);
	// 'confirm' opens OrderConfirmModal on its approve-all view (paid orders);
	// 'refund' jumps straight to the selectable view (confirmed / partially
	// refunded orders, where approve-all is moot).
	const [confirmMode, setConfirmMode] = useState('confirm');

	const hasRefundFailed = useMemo(() => orders.some((o) => o.status === 'refund_failed'), [orders]);
	const filterBuckets = hasRefundFailed ? [...ORDER_DISPLAY_BUCKETS, 'Refund failed'] : ORDER_DISPLAY_BUCKETS;

	// Active orders are pinned into their own "Action needed" section, so the
	// main table hides them by default. An explicit status filter overrides this
	// — selecting "Pending" shows them in the main table too.
	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		const allowed = statusBuckets.length ? expandBuckets(statusBuckets) : null;
		return orders
			.filter((o) => (allowed ? allowed.has(o.status) : !isActiveStatus(o.status)))
			.filter((o) => matchesQuery(o, q))
			.sort(sortByDateDesc);
	}, [orders, query, statusBuckets]);

	// With a status filter active the main table shows every matching order —
	// active ones included — so the pinned section is hidden entirely rather
	// than duplicating those rows or ignoring the filter (NEA-6278).
	const activeOrders = useMemo(() => {
		if (statusBuckets.length) return [];
		const q = query.trim().toLowerCase();
		return orders
			.filter((o) => isActiveStatus(o.status))
			.filter((o) => matchesQuery(o, q))
			.sort(sortByDateDesc);
	}, [orders, query, statusBuckets]);

	useEffect(() => {
		setPage(1);
	}, [query, statusBuckets]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const currentPage = Math.min(page, totalPages);
	const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

	const activeOrder = orders.find((o) => o.id === activeOrderId) || null;

	const loading = shopLoading || ordersLoading;
	if (loading) return <OrdersSkeleton />;

	if (orders.length === 0) {
		return (
			<div className="space-y-10">
				<header className="flex flex-wrap items-start justify-between gap-4">
					<div>
						<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Orders</h1>
						<p className="mt-2 text-muted-foreground">
							This is where you'll see customers reserving online to collect in store.
						</p>
					</div>
					<div className="ml-auto shrink-0">
						<PageScore />
					</div>
				</header>
				<EmptyState
					icon={ShoppingBag}
					eyebrow="Your first order is on the way"
					title="No orders yet — let's open the doors"
					body="Once your storefront is live, let locals buy or reserve from you. You'll get a ping the moment a local shopper reserves something to collect."
					primaryCta="Set up Click & Collect"
					primaryHref={shopPath('/channels/local-checkout')}
					secondaryCta="Set up Reservations"
					secondaryHref={shopPath('/channels/product-reservations')}
					previewTitle="A typical order day"
					preview={[
						{ icon: ShoppingBag, label: 'Sarah B. reserved a Yeti Rambler', detail: '£34.00 · Click & Collect' },
						{ icon: PackageCheck, label: 'Tom W. picked up Hydro Flask 32oz', detail: '£42.00 · Completed in store' },
						{ icon: Bell, label: 'New reservation — confirm in 1 tap', detail: 'Notifications keep you in the loop' }
					]}
				/>
			</div>
		);
	}

	// Both KPI fallbacks apply the same 12-month window as the insights Lambda
	// so the figure (and the "Past 12 months" label) stays consistent across
	// the load instead of briefly showing an all-time figure (NEA-5829).
	const windowStart = Date.parse(insightsWindow.from);
	const windowEnd = Date.parse(insightsWindow.to);
	// Mirrors the Lambda's `.between(start, end)` — bound both sides so a
	// future-dated `createdAt` doesn't get counted in either fallback.
	const isInWindow = (o) => {
		const ts = typeof o.createdAt === 'number' ? o.createdAt : Date.parse(o.createdAt || '');
		return !Number.isFinite(ts) || (ts >= windowStart && ts <= windowEnd);
	};
	// `ordersPlaced` + `reservationsPlaced` counts only orders whose orderType
	// is one of those two — matched in the fallback so the figure doesn't jump
	// when insights resolves.
	const totalOrders = insights
		? (insights.ordersPlaced || 0) + (insights.reservationsPlaced || 0)
		: orders.filter((o) => o.orderType === 'click-collect' || o.orderType === 'reservation').filter(isInWindow).length;
	// `totalOrderValue` comes from the Unify-specific `get-order-insights-unify`
	// Lambda — sums every order except `cancelled` / `refunded` and multiplies
	// by quantity, so the KPI card matches the visible table. Fall back to a
	// client-side sum applying the same exclusion list while insights is loading.
	const totalOrderValue =
		insights?.totalOrderValue ||
		formatCurrency(
			orders
				.filter((o) => o.status !== 'cancelled' && o.status !== 'refunded')
				.filter(isInWindow)
				.reduce((sum, o) => sum + netOrderTotal(o), 0),
			shop?.currency || 'GBP'
		);
	const completedCount = orders.filter((o) => o.status === 'completed').length;
	const completedPct = orders.length === 0 ? 0 : Math.round((completedCount / orders.length) * 100);

	const handleStartUpdate = (next) => {
		// Defer the modal open so the DropdownMenu (also a Radix layer) has time
		// to clean up its `body.style.pointerEvents = 'none'` lock before the
		// Dialog mounts and caches whatever it sees as "the original" state.
		// Without this defer, the Dialog caches 'none' on open and restores
		// the page to `pointer-events: none` on close, freezing the UI.
		setTimeout(() => setPendingUpdate(next), 0);
	};
	// Paid Click & Collect orders confirm line-by-line (approve all / partial
	// refund) via OrderConfirmModal; reservations keep the direct email flow.
	const needsLineConfirm = (o) => o.orderType === 'click-collect' && o.status === 'paid';
	const handleStartConfirm = (o) => {
		if (needsLineConfirm(o)) {
			setTimeout(() => {
				setConfirmMode('confirm');
				setConfirmTarget(o);
			}, 0);
		} else {
			handleStartUpdate({ ...o, status: 'confirmed' });
		}
	};
	const handleStartRefund = (o) => {
		setActiveOrderId(null);
		setTimeout(() => {
			setConfirmMode('refund');
			setConfirmTarget(o);
		}, 0);
	};
	const handleApproveAll = (o) => {
		// Hand off to the existing confirm mechanism (email modal → update).
		setConfirmTarget(null);
		setTimeout(() => setPendingUpdate({ ...o, status: 'confirmed' }), 0);
	};
	const handleSubmitRefund = async (items) => {
		try {
			await refundOrder(confirmTarget, items);
			toast({
				title: 'Refund processing',
				description: 'The order will update once the refund has gone through.',
				variant: 'success'
			});
		} catch (err) {
			toast({ title: 'Could not request refund', description: err?.message, variant: 'destructive' });
			throw err;
		}
	};
	const handleMarkCompleted = async (next) => {
		try {
			await updateOrder(next);
			toast({ title: 'Order marked as collected', variant: 'success' });
		} catch (err) {
			toast({ title: 'Could not update order', description: err?.message, variant: 'destructive' });
		}
	};
	const handleSubmitUpdate = async (next) => {
		try {
			await updateOrder(next);
			const isCancel = next.status === 'cancelled';
			const wasPaid = next.history?.some?.((h) => h.status === 'paid');
			toast({
				title: isCancel ? (wasPaid ? 'Order cancelled — refund issued' : 'Order cancelled') : 'Order confirmed',
				variant: 'success'
			});
		} catch (err) {
			toast({ title: 'Could not update order', description: err?.message, variant: 'destructive' });
			throw err;
		}
	};

	const pageNumbers = buildPageNumbers(currentPage, totalPages);

	const tableHeader = (
		<TableHeader>
			<TableRow className="hover:bg-transparent">
				<TableHead className="font-semibold text-foreground">Order</TableHead>
				<TableHead className="font-semibold text-foreground">Date</TableHead>
				<TableHead className="font-semibold text-foreground">Customer</TableHead>
				<TableHead className="font-semibold text-foreground">Product</TableHead>
				<TableHead className="font-semibold text-foreground">Price</TableHead>
				<TableHead className="font-semibold text-foreground">Payment</TableHead>
				<TableHead className="font-semibold text-foreground">Order Status</TableHead>
				<TableHead className="w-24 text-right font-semibold text-foreground">Actions</TableHead>
			</TableRow>
		</TableHeader>
	);

	const renderRows = (rows) =>
		rows.map((o) => {
			const product = o.products?.[0] || {};
			const extraLines = (o.products?.length || 0) - 1;
			const productLabel = extraLines > 0 ? `${product.name} +${extraLines} more` : product.name;
			const orderRef = o.pickupReference || o.id;
			const paymentLabel = o.orderType === 'click-collect' ? 'Paid online' : 'Pay in store';
			return (
				<TableRow
					key={o.id}
					className="cursor-pointer hover:bg-muted/40"
					onClick={() => setActiveOrderId(o.id)}
					data-testid={`order-row-${o.id}`}
				>
					<TableCell>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setActiveOrderId(o.id);
							}}
							className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline"
						>
							{orderRef}
							{o.notes ? <StickyNote className="h-3.5 w-3.5 text-primary/70" /> : null}
						</button>
					</TableCell>
					<TableCell className="whitespace-nowrap text-sm text-muted-foreground">{formatOrderDate(o)}</TableCell>
					<TableCell className="text-sm text-foreground">{o.customerName}</TableCell>
					<TableCell className="max-w-[280px] truncate text-sm text-foreground">{productLabel}</TableCell>
					<TableCell className="whitespace-nowrap text-sm font-semibold tabular-nums text-foreground">
						{formatCurrency(netOrderTotal(o), orderCurrency(o))}
					</TableCell>
					<TableCell className="whitespace-nowrap text-sm text-muted-foreground">{paymentLabel}</TableCell>
					<TableCell>
						<StatusPill status={o.status} refundPending={isRefundPending(o)} />
					</TableCell>
					<TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
						{/* Always reserve `h-8` so rows without an action button match the
						   height of rows that have one — otherwise the table visibly jumps
						   between status types. */}
						<div className="flex h-8 items-center justify-end">
							<RowActions
								order={o}
								canManage={canManage}
								onStartConfirm={handleStartConfirm}
								onStartUpdate={handleStartUpdate}
								onMarkCompleted={handleMarkCompleted}
								onStartRefund={handleStartRefund}
							/>
						</div>
					</TableCell>
				</TableRow>
			);
		});

	return (
		<div className="space-y-8">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Orders</h1>
					<p className="mt-2 max-w-2xl text-muted-foreground">
						Manage your customers' orders. New orders will show on the page automatically.
					</p>
				</div>
				<div className="ml-auto shrink-0">
					<PageScore />
				</div>
			</header>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
				<KpiSoftCard label="Total orders" value={totalOrders} sub="Past 12 months" />
				<KpiSoftCard label="Total order value" value={totalOrderValue} sub="Past 12 months" />
				<KpiSoftCard label="% completed" value={`${completedPct}%`} sub={`${completedCount} of ${orders.length} orders`} />
			</div>

			<div className="flex items-center gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Search for an order ID or customer"
						className="h-10 rounded-xl bg-card pl-10"
					/>
				</div>
				<StatusFilter buckets={filterBuckets} selected={statusBuckets} onChange={setStatusBuckets} />
			</div>

			{activeOrders.length > 0 ? (
				<section className="space-y-3">
					<div className="flex items-center gap-2">
						<h2 className="text-lg font-bold tracking-tight text-foreground">Action needed</h2>
						<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground">
							{activeOrders.length}
						</span>
					</div>
					<SoftCard className="overflow-hidden !p-0">
						<Table>
							{tableHeader}
							<TableBody>{renderRows(activeOrders)}</TableBody>
						</Table>
					</SoftCard>
				</section>
			) : null}

			<section className="space-y-3">
				{activeOrders.length > 0 ? <h2 className="text-lg font-bold tracking-tight text-foreground">All orders</h2> : null}
				<SoftCard className="overflow-hidden !p-0">
					<Table>
						{tableHeader}
						<TableBody>
							{pageRows.length > 0 ? (
								renderRows(pageRows)
							) : (
								<TableRow className="hover:bg-transparent">
									<TableCell colSpan={8} className="py-12 text-center text-muted-foreground">
										No orders match your filters.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</SoftCard>
			</section>

			{totalPages > 1 ? (
				<Pagination>
					<PaginationContent>
						<PaginationItem>
							<PaginationLink
								href="#"
								size="default"
								aria-label="Go to previous page"
								aria-disabled={currentPage === 1}
								onClick={(e) => {
									e.preventDefault();
									setPage((p) => Math.max(1, p - 1));
								}}
								className={cn('gap-1 pl-2.5', currentPage === 1 ? 'pointer-events-none opacity-50' : '')}
							>
								<ChevronLeft className="h-4 w-4" />
								<span>Previous</span>
							</PaginationLink>
						</PaginationItem>
						{pageNumbers.map((p, i) =>
							p === 'ellipsis' ? (
								<PaginationItem key={`e-${i}`}>
									<PaginationEllipsis />
								</PaginationItem>
							) : (
								<PaginationItem key={p}>
									<PaginationLink
										href="#"
										isActive={p === currentPage}
										onClick={(e) => {
											e.preventDefault();
											setPage(p);
										}}
									>
										{p}
									</PaginationLink>
								</PaginationItem>
							)
						)}
						<PaginationItem>
							<PaginationLink
								href="#"
								size="default"
								aria-label="Go to next page"
								aria-disabled={currentPage === totalPages}
								onClick={(e) => {
									e.preventDefault();
									setPage((p) => Math.min(totalPages, p + 1));
								}}
								className={cn('gap-1 pr-2.5', currentPage === totalPages ? 'pointer-events-none opacity-50' : '')}
							>
								<span>Next</span>
								<ChevronRight className="h-4 w-4" />
							</PaginationLink>
						</PaginationItem>
					</PaginationContent>
				</Pagination>
			) : null}

			<OrderDetailModal
				order={activeOrder}
				shop={shop}
				open={!!activeOrder}
				onOpenChange={(v) => !v && setActiveOrderId(null)}
				onConfirm={(o) => {
					// Close the detail modal first, then open the next modal on the
					// next tick. Two Radix Dialogs swapping in the same render can
					// leave `body { pointer-events: none }` stuck.
					setActiveOrderId(null);
					if (needsLineConfirm(o)) {
						setTimeout(() => {
							setConfirmMode('confirm');
							setConfirmTarget(o);
						}, 0);
					} else {
						setTimeout(() => setPendingUpdate({ ...o, status: 'confirmed' }), 0);
					}
				}}
				onRefundItems={handleStartRefund}
				onComplete={async (o) => {
					// The detail modal hands back the order in its current state
					// (still `confirmed`). The parent owns the status transition.
					await handleMarkCompleted({ ...o, status: 'completed' });
					setActiveOrderId(null);
				}}
				onCancel={(o) => {
					setActiveOrderId(null);
					setTimeout(() => setPendingUpdate({ ...o, status: 'cancelled' }), 0);
				}}
			/>

			<OrderConfirmModal
				order={confirmTarget}
				shop={shop}
				open={!!confirmTarget}
				onOpenChange={(v) => !v && setConfirmTarget(null)}
				onApproveAll={handleApproveAll}
				onSubmitRefund={handleSubmitRefund}
				initialMode={confirmMode}
			/>

			<OrderUpdateModal
				pendingUpdate={pendingUpdate}
				shop={shop}
				onClose={() => setPendingUpdate(null)}
				onSubmit={handleSubmitUpdate}
			/>
		</div>
	);
}
